import {
  Children,
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./Carousel.module.css";
import classNames from "classnames";

export type CarouselHandle = {
  next: () => void;
  prev: () => void;
  scrollToIndex: (index: number, behavior?: ScrollBehavior) => void;
};

type Props = {
  children: ReactNode;
  // Fixed number of items visible at once. Ignored when autoWidth is set.
  itemsPerView?: number;
  // Size each item to its own content instead of a fraction of the viewport.
  autoWidth?: boolean;
  // Space between items, in px.
  gap?: number;
  // Items advanced per next()/prev(). Defaults to itemsPerView.
  slideBy?: number;
  className?: string;
  itemClassName?: string;
  onEdgeChange?: (edges: { canPrev: boolean; canNext: boolean }) => void;
};

// A dependency-free horizontal carousel: a native scroll-snap track plus an
// imperative next/prev handle for callers that render their own arrow buttons.
// Uses scrollIntoView (not signed scrollBy) so RTL needs no scroll-sign math.
function Carousel(
  {
    gap = 24,
    itemsPerView = 1,
    slideBy: step = itemsPerView,
    onEdgeChange,
    ...props
  }: Props,
  ref: React.Ref<CarouselHandle>
) {
  const trackRef = useRef<HTMLDivElement>(null);
  const lastActiveIndexRef = useRef(-1);

  const getItems = () => Array.from(trackRef.current?.children ?? []) as HTMLElement[];

  const getActiveIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return 0;
    }
    const start = Math.abs(track.scrollLeft);
    let best = 0;
    let bestDistance = Infinity;
    getItems().forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - start);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }, []);

  const syncState = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    // Math.abs so the same logic works under RTL (negative scrollLeft).
    const start = Math.abs(track.scrollLeft);
    const canPrev = start > EDGE_EPSILON;
    const canNext = start < track.scrollWidth - track.clientWidth - EDGE_EPSILON;
    onEdgeChange?.({ canPrev, canNext });
    const activeIndex = getActiveIndex();
    if (activeIndex !== lastActiveIndexRef.current) {
      lastActiveIndexRef.current = activeIndex;
    }
  }, [onEdgeChange, getActiveIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(syncState);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(() => syncState());
    observer.observe(track);
    syncState();
    return () => {
      track.removeEventListener("scroll", onScroll);
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [syncState]);

  const scrollToIndex = useCallback((index: number, behavior?: ScrollBehavior) => {
    const items = getItems();
    const target = items[Math.max(0, Math.min(index, items.length - 1))];
    target?.scrollIntoView({
      inline: "start",
      block: "nearest",
      behavior: behavior ?? (prefersReducedMotion() ? "auto" : "smooth"),
    });
  }, []);

  const next = useCallback(
    () => scrollToIndex(getActiveIndex() + step),
    [scrollToIndex, getActiveIndex, step]
  );
  const prev = useCallback(
    () => scrollToIndex(getActiveIndex() - step),
    [scrollToIndex, getActiveIndex, step]
  );

  useImperativeHandle(ref, () => ({ next, prev, scrollToIndex }), [
    next,
    prev,
    scrollToIndex,
  ]);

  const trackClassName = classNames(styles.carouselTrack, props.className, {
    [styles.carouselAutoWidth]: props.autoWidth,
  });

  const style = {
    "--carousel-gap": `${gap}px`,
    "--carousel-items": String(itemsPerView),
  } as CSSProperties;

  return (
    <div ref={trackRef} className={trackClassName} style={style}>
      {Children.map(props.children, child => (
        <div className={classNames(styles.carouselItem, props.itemClassName)}>
          {child}
        </div>
      ))}
    </div>
  );
}

// Companion hook bundling the consumer-side wiring: the handle ref, edge
// tracking, and stable command callbacks. Spread `carouselProps` onto the
// <Carousel> and drive custom buttons from the rest.
export function useCarousel() {
  const ref = useRef<CarouselHandle>(null);
  const [edges, setEdges] = useState({ canPrev: false, canNext: false });

  const next = useCallback(() => ref.current?.next(), []);
  const prev = useCallback(() => ref.current?.prev(), []);
  const scrollToIndex = useCallback(
    (index: number, behavior?: ScrollBehavior) =>
      ref.current?.scrollToIndex(index, behavior),
    []
  );

  return {
    canPrev: edges.canPrev,
    canNext: edges.canNext,
    next,
    prev,
    scrollToIndex,
    carouselProps: { ref, onEdgeChange: setEdges },
  };
}

// A tolerance (px) so sub-pixel scroll offsets don't flip the edge flags.
const EDGE_EPSILON = 1;

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export default forwardRef<CarouselHandle, Props>(Carousel);
